import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Captions, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Super-admin panel: status of Vimeo transcripts used by LIA to answer and recommend videos. */
export function VideoTranscriptsPanel() {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const { data } = useQuery({
    queryKey: ["content-transcripts-status"],
    queryFn: async () => {
      const { data, error } = await supabase.from("content_transcripts").select("status");
      if (error) throw error;
      const c = { ok: 0, pending: 0, missing: 0, error: 0 } as Record<string, number>;
      (data || []).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
      return c;
    },
  });

  const run = async () => {
    setRunning(true);
    try {
      const { data: res, error } = await supabase.functions.invoke("sync-vimeo-transcripts", { body: { limit: 40 } });
      if (error) {
        const d = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
        throw new Error(d);
      }
      toast.success(`Transcripciones: ${res.ok} importadas, ${res.missing} sin transcripción. Pendientes: ${res.remaining}`);
    } catch (e) {
      toast.error(`No se pudieron importar: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["content-transcripts-status"] });
    }
  };

  if (!data) return null;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex items-center gap-3">
          <Captions className="h-5 w-5 text-primary" />
          <div>
            <p className="font-medium">Transcripciones de videos para LIA</p>
            <p className="text-sm text-muted-foreground">LIA las usa para responder y recomendar videos. Se actualizan solas cada hora.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{data.ok} listas</Badge>
          <Badge variant="outline">{data.pending} pendientes</Badge>
          <Badge variant="outline">{data.missing} sin transcripción</Badge>
          {data.error > 0 && <Badge variant="destructive">{data.error} con error</Badge>}
          <Button size="sm" variant="outline" onClick={run} disabled={running || data.pending + data.error === 0}>
            {running && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Importar ahora
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
